/* Copyright (c) 2020-present CloudIO Inc */

import {
  Button,
  Dialog,
  IconButton,
  InputBase,
  Menu,
  MenuItem,
  Theme,
  Typography,
} from '@mui/material';
import { makeStyles } from '@mui/styles';
import { IconValue } from 'componentregistry';
import { IconType } from 'componentregistry/icons/types';
import VirtualList from 'components/DataGrid/grid/List';
import { useDeferredValue } from 'experimental/ExperimentalReact';
import { motion } from 'framer-motion';
import useWindowSize from 'hooks/useWindowSize';
import Icon from 'icons/Icon';
import { allIcons } from 'icons/IconOptions';
import * as React from 'react';
import './fa/css/all.css';
import categories from './IconCategories';

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    position: 'relative',
    height: '100%',
    margin: '0 auto',
    padding: 0,
  },
  icon: {
    cursor: 'pointer',
    fontSize: '2em',
  },
  cell: {
    flexBasis: 'auto',
    flexGrow: 0,
    minWidth: 96,
    minHeight: 96,
    maxWidth: 96,
    maxHeight: 96,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  title: {
    position: 'sticky',
    top: 0,
    padding: theme.spacing(2),
    background: theme.palette.background.paper,
    zIndex: 1,
  },
  search: {
    padding: theme.spacing(1),
    flex: 1,
    fontSize: '2em',
  },
  body: {
    position: 'absolute',
    top: 90,
    left: 0,
    right: 0,
    bottom: 0,
    overflow: 'hidden',
  },
  searchWrapper: {
    zIndex: 2,
    position: 'sticky',
    top: 0,
    display: 'flex',
    alignItems: 'center',
    padding: theme.spacing(3),
    background: theme.palette.background.paper,
    height: 90,
  },
  header: {
    zIndex: 2,
    position: 'sticky',
    top: 0,
    display: 'flex',
    alignItems: 'center',
    paddingLeft: theme.spacing(3),
    background: theme.palette.background.paper,
    borderTop: `1px solid ${theme.palette.divider}`,
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  iconButton: {
    padding: 10,
    fontSize: '2em',
  },
  close: {
    position: 'absolute',
    top: 0,
    right: 0,
    fontSize: '4em',
    padding: 22.5,
    zIndex: 3,
  },
  block: {
    display: 'flex',
    flexBasis: '100%',
    maxWidth: '100%',
    flexGrow: 0,
  },
  row: {
    display: 'flex',
  },
  count: {
    color: theme.palette.text.secondary,
  },
}));

const Content = React.memo(
  ({
    onChange,
    data,
    width,
    height,
  }: {
    width: number;
    height: number;
    onChange: (val: IconValue) => void;
    data: {
      totalCount: number;
      filterCount: number;
      items: (
        | string[]
        | {
            iconType: IconType;
            title: string;
          }
      )[];
      headers: {
        iconType: IconType;
        title: string;
        startIndex: number;
        endIndex: number;
      }[];
    };
  }) => {
    const classes = useStyles();
    const ref = React.useRef<{
      iconType: IconType;
      title: string;
      rowIndex: number;
    }>({
      iconType: 'light',
      title: '',
      rowIndex: 0,
    });
    return (
      <div className={classes.body}>
        <VirtualList
          CellComponent={({ rowIndex, style }) => {
            const val = data.items[rowIndex];
            if (Array.isArray(val)) {
              const header = data.headers.find(
                (h) => h.startIndex <= rowIndex && h.endIndex >= rowIndex,
              );
              if (!header) {
                return null;
              }

              return (
                <>
                  {/* {firstRow ? (
                    <div
                      key={header.title}
                      style={{ ...style, position: 'sticky', top: 0 }}
                      className={classes.header}
                      data-start={header.startIndex}
                      data-end={header.endIndex}
                      data-row={rowIndex}
                    >
                      <Typography variant="h5">{header.title}</Typography>
                    </div>
                  ) : null} */}
                  <div
                    data-row={rowIndex}
                    style={style}
                    className={classes.row}
                  >
                    {val.map((icon) => (
                      <div key={icon} className={classes.cell}>
                        <motion.i
                          data-testid={`${icon}-${header.iconType}-icon`}
                          onClick={(e) => {
                            e.stopPropagation();
                            onChange({
                              iconName: icon,
                              iconType: header.iconType,
                            });
                          }}
                          whileHover={{ scale: 2 }}
                          data-tip={icon}
                          className={`${classes.icon} ${
                            allIcons[header.iconType].code
                          } fa-${icon}`}
                        />
                      </div>
                    ))}
                  </div>
                </>
              );
            }
            ref.current = { rowIndex, ...val };
            return (
              <div
                key={val.title}
                data-row={rowIndex}
                style={
                  style
                  // firstRow ? { ...style, position: 'sticky', top: 0 } : style
                }
                className={classes.header}
              >
                <Typography variant="h5">{val.title}</Typography>
              </div>
            );
          }}
          rowHeight={96}
          height={height}
          totalRows={data.items.length}
          width={width}
          custom
        />
      </div>
    );
  },
);

export default function IconPickerDialog({
  open,
  onClose,
  onChange,
}: {
  open: boolean;
  onChange: (icon: IconValue) => void;
  onClose: () => void;
}) {
  const classes = useStyles();
  const [filter, setFilter] = React.useState('');
  const filterLC = useDeferredValue(filter.toLowerCase());
  const [selectedIndex, setSelectedIndex] = React.useState(-1);
  const [w, height] = useWindowSize();
  const width = Math.min(1200, w - 180);
  const cols = Math.floor(width / 96);

  const data = React.useMemo(() => {
    const iconTypes = Object.keys(allIcons) as (keyof typeof allIcons)[];
    let totalCount = 0;
    let filterCount = 0;

    const items: ({ iconType: IconType; title: string } | string[])[] = [];
    const headers: {
      iconType: IconType;
      title: string;
      startIndex: number;
      endIndex: number;
    }[] = [];
    iconTypes
      .filter((iconType) => iconType !== 'cloudio')
      .forEach((iconType) => {
        totalCount += allIcons[iconType].list.length;
        const icons = allIcons[iconType].list.filter(
          (icon) =>
            (selectedIndex === -1 ||
              categories[selectedIndex].icons.includes(icon)) &&
            (!filterLC || icon.includes(filterLC)),
        );

        if (icons.length) {
          filterCount += icons.length;
          items.push({ title: allIcons[iconType].title, iconType });
          headers.push({
            title: allIcons[iconType].title,
            iconType,
            startIndex: items.length,
            endIndex: items.length + Math.ceil(icons.length / cols) - 1,
          });
          new Array(Math.ceil(icons.length / cols)).fill([]).forEach((_) => {
            items.push(icons.splice(0, cols));
          });
        }
      });
    return { totalCount, filterCount, items, headers };
  }, [selectedIndex, filterLC, cols]);

  const ref = React.useRef<HTMLInputElement>(null);
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const handleClickListItem = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuItemClick = (
    event: React.MouseEvent<HTMLElement>,
    index: number,
  ) => {
    setSelectedIndex(index);
    setAnchorEl(null);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  return (
    <Dialog fullScreen open={open} onClose={onClose}>
      <div
        className={classes.root}
        role="none"
        onClick={() => {
          ref.current?.focus();
        }}
      >
        <div className={classes.searchWrapper} style={{ width, height: 90 }}>
          <IconButton className={classes.iconButton} aria-label="search">
            <Icon width={32} height={32} iconName="search" />
          </IconButton>
          <Button
            color="primary"
            onClick={handleClickListItem}
            data-tip="Icon Category"
            variant="contained"
            endIcon={
              <Icon
                iconName="caret-down"
                iconType="solid"
                parentColor="primary"
              />
            }
          >
            {selectedIndex !== -1 ? categories[selectedIndex].label : 'All'}
          </Button>
          <Menu
            id="lock-menu"
            anchorEl={anchorEl}
            open={!!anchorEl}
            onClose={handleClose}
            MenuListProps={{
              'aria-labelledby': 'lock-button',
              role: 'listbox',
            }}
          >
            <MenuItem
              key="all"
              selected={-1 === selectedIndex}
              onClick={(event) => handleMenuItemClick(event, -1)}
            >
              All
            </MenuItem>
            {categories.map(({ label }, index) => (
              <MenuItem
                key={label}
                selected={index === selectedIndex}
                onClick={(event) => handleMenuItemClick(event, index)}
              >
                {label}
              </MenuItem>
            ))}
          </Menu>
          <InputBase
            inputRef={ref}
            className={classes.search}
            placeholder="Search..."
            value={filter ?? ''}
            onChange={(e) => setFilter(e.target.value)}
            autoFocus
          />
          <Typography className={classes.count} variant="subtitle2">
            {`${
              data.filterCount === data.totalCount
                ? data.totalCount
                : `${data.filterCount} / ${data.totalCount}`
            } icons`}
          </Typography>
        </div>
        <Content
          width={width}
          height={height - 90}
          data={data}
          onChange={onChange}
        />
      </div>
      <IconButton
        onClick={onClose}
        className={classes.close}
        aria-label="close"
        size="medium"
      >
        <span aria-hidden="false" style={{ display: 'none' }}>
          Close
        </span>
        <Icon width={32} height={32} iconName="times" />
      </IconButton>
    </Dialog>
  );
}
